// @ts-strict-ignore
import { BaseArticleManager } from '@project/components/article/manager/base-article-manager';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleConstants } from '@article/constants/article-constants';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleManager } from '@article/manager/article.manager';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { InsertArticleService } from '../service/insert-article.service';
import { Log } from '@shared/debug/log';
import { InsertedArticleData } from '../processor/i-article-location.processor';
import { SelectionModelService } from '../service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';

export class BlockingZoneArticleManager extends BaseArticleManager {
  private static readonly LANGUAGE_KEY_BLOCKING_ZONE_ERROR_TITLE: string = 'blockingZoneArticleManager.error.title.blocking_zone';
  private static readonly LANGUAGE_KEY_EMPTY_ZONE_ERROR_TITLE: string = 'blockingZoneArticleManager.error.title.empty_zone';
  private static readonly ERROR_TITLE_DEFAULT: string = 'Failed to insert article';

  private _applyBlockingZoneType: string;
  private _zoneArticleType: string;
  private _isLeadingValue: boolean = false;


  constructor(
    projectModel: ProjectModel,
    undoRedoHelper: UndoRedoHelper,
    alertHelper: AlertHelper,
    undoRedoUtils: UndoRedoUtils,
    insertArticleUtils: InsertArticleUtils,
    private selectionModelService: SelectionModelService,
    private articleManager: ArticleManager,
    private localeResourceManager: LocaleResourceManager,
    private containerService: ContainerService,
    protected _insertArticleService: InsertArticleService,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService
  ) {
    super(projectModel, undoRedoHelper, alertHelper, undoRedoUtils, insertArticleUtils);
  }

  public async insertZoneArticle(
    blockingZoneArticle: ArticleVO,
    parentUsage: ArticleUsageVO,
    length: number,
    offset: number,
    roundOffsetValue: boolean = false,
    isLeadingValue: boolean = false,
    messageType: string = 'insertBlockingZoneArticle'
  ): Promise<InsertedArticleData> {

    this._articleLocation = this.createOnRailArticleLocation(parentUsage, offset);
    this._zoneArticleType = blockingZoneArticle.type.displayName;
    this._isLeadingValue = isLeadingValue;

    this.projectModel.lastBlockingZoneLength = length;
    this.projectModel.lastBlockingZoneOffset = roundOffsetValue ? MathUtils.roundNumberValue(offset, 4) : offset;

    let insertedData: InsertedArticleData;
    try {
      insertedData = await this._insertArticleService.insertArticleByArticleLocation(blockingZoneArticle, this._articleLocation, messageType);
      await this.blockingZoneInserted(insertedData);
    } catch (error) {
      Log.error('Could not insert article: ', error);
      await this.blockingZoneInserted_failure(error);
      throw error;
    }
    return insertedData
  }

  public async recreateArticle(articleUsage: ArticleUsageVO, length: number, offset: number): Promise<void> {
    this.selectionModelService.removeAllSelections();

    const article: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
    const parentUsage: ArticleUsageVO = this.getParentArticleUsage(articleUsage);
    if (!parentUsage) {
      return;
    }
    if (ArticleUtils.isBlockingZone(article)) {
      await this.recreateBlockingZone(article, articleUsage, parentUsage, length, offset);
    } else if (ArticleUtils.isEmptyZoneArticle(article)) {
      await this.startRecreateOperation(article, articleUsage, parentUsage, length, offset);
    }
  }

  private async recreateBlockingZone(article: ArticleVO, articleUsage: ArticleUsageVO, parentUsage: ArticleUsageVO, length: number, offset: number): Promise<void> {
    this._applyBlockingZoneType = null;
    const oldBlockingZoneType: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.BLOCKING_ZONE_TYPE
    );
    if (oldBlockingZoneType) {
      const oldOffset: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET);
      if (oldOffset === offset) {
        this._applyBlockingZoneType = oldBlockingZoneType;
      } else if (oldBlockingZoneType === ArticleConstants.BLOCKING_ZONE_TYPE_END) {
        const parentLength: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(parentUsage, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH);
        if (MathUtils.equalsNumber(offset + length, parentLength)) {
          this._applyBlockingZoneType = oldBlockingZoneType;
        }
      }
    }
    await this.startRecreateOperation(article, articleUsage, parentUsage, length, offset);
  }

  private async startRecreateOperation(article: ArticleVO, articleUsage: ArticleUsageVO, parentUsage: ArticleUsageVO, length: number, offset: number): Promise<void> {
    const isLeadingValue: boolean = ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(articleUsage, ArticleUsageInfoPropertyDef.IS_LEADING_VALUE);
    const usageIdsToDetach: string[] = ReferenceArticleUsageUtils.getForwardReferenceArticleUsageIds(articleUsage);
    usageIdsToDetach.push(articleUsage.id);
    this._detachOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds(usageIdsToDetach);
    const detachedArticle = await this.detachArticleUsageService.detachArticleUsageFromProjectById(articleUsage.id);
    if (detachedArticle) {
      this.applicationRefreshService.notifyProjectChange();
    }
    await this.insertZoneArticle(article, parentUsage, length, offset, false, isLeadingValue);
  }

  private async blockingZoneInserted(data: any = {}): Promise<void> {
    const articleLocation: ArticleLocationVO = data.articleLocation;
    if (articleLocation !== this._articleLocation) {
      return;
    }
    const addedArticleGUIDs: string[] = data.insertedArticleGUIDs;
    const createdContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(addedArticleGUIDs[0]);
    if (this._applyBlockingZoneType) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
        createdContainer.articleUsage,
        ArticleUsageInfoPropertyDef.BLOCKING_ZONE_TYPE,
        this._applyBlockingZoneType
      );
      this._applyBlockingZoneType = null;
    }
    ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(
      createdContainer.articleUsage, ArticleUsageInfoPropertyDef.IS_LEADING_VALUE, this._isLeadingValue);
    await this.pushInsertArticleOperation(addedArticleGUIDs, data.moveArticlesMap, data.undoOperations);
    this.reset();
  }

  private async blockingZoneInserted_failure(messageData: any = {}): Promise<void> {
    if (messageData && messageData.hasOwnProperty('message')) {
      const message: string = messageData.message;
      await this.finalizeFailureProcessingArticle(message, this.getErrorTitleByZoneType(this._zoneArticleType));
    }
  }

  protected reset(): void {
    super.reset();
    this._applyBlockingZoneType = null;
    this._zoneArticleType = null;
    this._isLeadingValue = false;
  }

  private getErrorTitleByZoneType(zoneType: string): string {
    let errorLanguageKey: string = null;
    switch (zoneType) {
      case ArticleTypeConstants.TYPE_BLOCKING_ZONE:
        errorLanguageKey = BlockingZoneArticleManager.LANGUAGE_KEY_BLOCKING_ZONE_ERROR_TITLE;
        break;
      case ArticleTypeConstants.TYPE_EMPTY_ZONE:
        errorLanguageKey = BlockingZoneArticleManager.LANGUAGE_KEY_EMPTY_ZONE_ERROR_TITLE;
        break;
    }
    return errorLanguageKey == null ? BlockingZoneArticleManager.ERROR_TITLE_DEFAULT : this.localeResourceManager.getValueByKey(errorLanguageKey);
  }
}
